import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import classNames from "classnames";
import {
	Anchor,
	AppIcon,
	Button,
	ButtonKind,
	FeaturedBanner,
	Icon,
	FontIconName,
	IconButton,
	TagModel,
	TagList,
} from "client/Components";
import { useCallbackWithoutArguments } from "client/Hooks";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	useMemo
} from "react";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import { AppColorViewModel, AppType } from "client/Api/Apps/common";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./AppCard.module.less";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import { getUserLicenseName } from "client/Features/FeaturePackages/misc";
import { TagViewModel } from "client/Api/Tags/tags";

export type PrimaryAction = {
	key: LocalizationKey;
	kind: ButtonKind;
	handler(): void;
	isDisabled?: boolean;
	trackingId: string | undefined;
};

export type SecondaryAction = {
	icon: FontIconName;
	handler(): void;
	trackingId: string | undefined;
	tooltipKey: LocalizationKey;
};

export type AccessConfiguration = {
	hasMaintenance: boolean;
	hasConditionalAccess: boolean;
	hasOwnerPermissions: boolean;
};

export type AppCardSize =
	| "normal"
	| "short";

export type AppCardProps = {
	icon: AppImageDisplayInfo;
	color: AppColorViewModel;
	userLicense: UserLicense | null;
	name: string;
	category: string | undefined;
	appKind?: AppType;
	access?: AccessConfiguration;
	isFeaturedAsNew?: boolean;
	size?: AppCardSize;
	primaryAction?: PrimaryAction;
	secondaryAction?: SecondaryAction;
	trackingId: string;
	tags?: readonly TagViewModel[];
};

export const AppCard = (props: AppCardProps): ReactElement => {
	const {
		icon,
		color,
		userLicense,
		name,
		category,
		appKind,
		access = {
			hasConditionalAccess: false,
			hasMaintenance: false,
			hasOwnerPermissions: false
		},
		isFeaturedAsNew = false,
		size = "normal",
		primaryAction,
		secondaryAction,
		tags,
		trackingId
	} = props;

	const { colorClass, colorStyle } = getReactColorAttributes(color);

	const accessIcons: FontIconName[] = [];
	if (access.hasMaintenance) {
		accessIcons.push("screwdriver-wrench");
	}
	if (access.hasConditionalAccess) {
		accessIcons.push("shield-check");
	}
	if (access.hasOwnerPermissions) {
		accessIcons.push("user-tie");
	}

	const primaryActionHandler: (() => void) | undefined = useCallbackWithoutArguments(primaryAction?.handler);

	const tagModels = useMemo(
		(): (readonly TagModel<number>[]) | undefined => {
			return tags?.map((tag: TagViewModel): TagModel<number> => ({
				text: tag.name,
				value: tag.id,
				color: "secondary"
			}));
		},
		[tags]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				colorClass,
				appCardSizeToStyleMap[size]
			)}
			style={colorStyle}
		>
			{isFeaturedAsNew && (
				<FeaturedBanner kind="new" />
			)}
			<div className={styles.content}>
				<Row kind="icons">
					<div className={styles.iconContainer}>
						<div className={styles.icon}>
							<AppIcon value={icon} color="inherit" />
						</div>
					</div>
					{accessIcons.length > 0 && (
						<div className={styles.settingIcons}>
							{accessIcons.map((x, i) => (
								<Icon
									key={i}
									value={{
										kind: "font",
										name: x,
										color: "danger"

									}}
									size="large"
								/>
							))}
						</div>
					)}
				</Row>

				<Row>
					<h4>
						{primaryActionHandler
							?	(
								<Anchor
									trackingId="name"
									className={
										classNames(
											styles.name,
											styles.name_link
										)
									}
									onClick={primaryActionHandler}
									title={name}
								>
									{name}
								</Anchor>
							)
							: (
								<span
									{...setTrackingId("name")}
									title={name}
									className={styles.name}
								>
									{name}
								</span>
							)
						}
					</h4>
				</Row>

				{typeof appKind !== "undefined" && (
					<Row>
						<SubText isBold trackingId="app-kind">
							{appKindToNameMap[appKind]}
						</SubText>
					</Row>
				)}

				{category && (
					<Row>
						<SubText
							isBold
							trackingId="category"
						>
							{category}
						</SubText>
					</Row>
				)}

				{isNotNil(userLicense) && (
					<Row>
						<SubText trackingId="user-license">
							{getUserLicenseName(userLicense)}
						</SubText>
					</Row>
				)}
				{isNotNil(tagModels) && (
					<Row kind="tags">
						<TagList
							tags={tagModels}
							trackingId="tags"
							showMoreTagsColor="secondary"
						/>
					</Row>
				)}

			</div>

			{ (primaryAction || secondaryAction) && (
				<Row>
					<div className={styles.buttons}>
						<div className={styles.mainButton}>
							{ primaryAction && (
								<Button
									kind={primaryAction.kind}
									text={localize(primaryAction.key)}
									width="fill"
									onClick={primaryActionHandler}
									isDisabled={primaryAction.isDisabled}
									trackingId={primaryAction.trackingId}
								/>
							)}
						</div>

						{ secondaryAction && (
							<IconButton
								kind="secondary"
								icon={secondaryAction.icon}
								onClick={secondaryAction.handler}
								trackingId={secondaryAction.trackingId}
								tooltip={localize(secondaryAction.tooltipKey)}
								shape="round"
							/>
						)}
					</div>
				</Row>
			)}
		</div>
	);
};
AppCard.displayName = "AppCard";

type RowKind =
	| "regular"
	| "icons"
	| "tags";

type RowProps = {
	kind?: RowKind;
};

const Row = (props: PropsWithChildren<RowProps>): ReactElement => {
	const {
		kind = "regular",
		children
	} = props;

	return (
		<div
			className={
				classNames(
					styles.row,
					rowKindToStyleMap[kind]
				)
			}
		>
			{ children }
		</div>
	);
};
Row.displayName = "Row";

const rowKindToStyleMap: { [key in RowKind]: string | undefined; } = {
	regular: undefined,
	icons: styles.row_icons,
	tags: styles.row_tags,
};

type SubTextProps = {
	isBold?: boolean;
	trackingId?: string;
};

const SubText = (props: PropsWithChildren<SubTextProps>): ReactElement => {
	const {
		isBold,
		trackingId,
		children
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.subText,
				{
					[styles.subText_bold]: isBold,
				}
			)}
		>
			{ children }
		</div>
	);
};
SubText.displayName = "SubText";

export const appKindToNameMap: { [key in AppType]: string } = {
	[AppType.Unknown]: localize("_Unknown"),
	[AppType.Url]: localize("_Shortcut"),
	[AppType.Email]: localize("_Email"),
	[AppType.Documents]: localize("_Documents"),
	[AppType.Office365Word]: localize("_Word"),
	[AppType.Office365Excel]: localize("_Excel"),
	[AppType.Office365PowerPoint]: localize("_PowerPoint"),
	[AppType.Office365OneNote]: localize("_OneNote"),
	[AppType.Office365LyncMeeting]: localize("_SkypeMeeting"),
	[AppType.Office365PeopleScreen]: localize("_People"),
	[AppType.Office365Calendar]: localize("_Calendar"),
	[AppType.CitrixXenApp]: localize("_CitrixXenApp"),
	[AppType.Rss]: localize("_RSS"),
	[AppType.AzureApp]: localize("_AzureApp"),
	[AppType.ClientlessRdp]: localize("_ClientlessRDP"),
	[AppType.PowerBi]: localize("_PowerBI"),
	[AppType.Yammer]: localize("_VivaEngage"),
	[AppType.Embed]: localize("_WebContent"),
	[AppType.ToDo]: localize("_ToDo"),
	[AppType.Google]: localize("_Google"),
	[AppType.TopDesk]: localize("_TopDesk"),
	[AppType.ConnectWise]: localize("_ConnectWise"),
	[AppType.Teams]: localize("_Teams"),
	[AppType.SharePoint]: localize("_SharePoint"),
	[AppType.AddressBook]: localize("_AddressBook"),
	[AppType.HowTo]: localize("_HowTo"),
	[AppType.Hub]: localize("_TheHub"),
	[AppType.LocalApp]: localize("_LocalApp"),
	[AppType.WindowsVirtualDesktop]: localize("_AzureVirtualDesktop"),
	[AppType.VilansKick]: localize("_VilansKick"),
	[AppType.Birthday]: localize("_Birthday"),
	[AppType.Autotask]: localize("_Autotask"),
	[AppType.YouTube]: localize("_YouTube"),
	[AppType.NedapOns]: localize("_NedapOns"),
	[AppType.Afas]: localize("_Afas"),
	[AppType.Zaaksysteem]: localize("_Zaaksysteem"),
	[AppType.Zermelo]: localize("_Zermelo"),
	[AppType.ZenyaDoc]: localize("_ZenyaDoc"),
	[AppType.Liquit]: localize("_Recast"),
	[AppType.VmwareHorizonApp]: localize("_OmnissaHorizon"),
	[AppType.Magister]: localize("_Magister"),
	[AppType.Integration]: localize("_IntegrationApp")
};

const appCardSizeToStyleMap: { [key in AppCardSize]: string | undefined; } = {
	normal: undefined,
	short: styles.root_short
};